import { useEffect } from 'react';

import { usePathname } from 'expo-router';

import { useExperiments } from 'state/experiments';
import { RunningExperiments } from 'state/experiments';
import { routes } from 'utils/routing';

type CheckoutV2ExperimentGate = {
  isReady: boolean;
  isTreatment: boolean;
};

type ResolveCheckoutV2ExperimentGateArgs = {
  isExperimentLoading: boolean;
  pathname: string;
  variantValue?: string;
};

let lockedCheckoutV2TreatmentDecision: boolean | undefined;
let hasLoggedCheckoutV2ExposureForLockedDecision = false;
let checkoutV2FlowConsumerCount = 0;
let checkoutV2ExperimentGateResetTimeout: ReturnType<typeof setTimeout> | undefined;

const isCheckoutV2FlowRoute = (pathname: string) =>
  pathname === routes.cart ||
  pathname.startsWith(routes.cartPayment) ||
  pathname.startsWith(routes.cartCheckout);

const resetCheckoutV2ExperimentGate = () => {
  lockedCheckoutV2TreatmentDecision = undefined;
  hasLoggedCheckoutV2ExposureForLockedDecision = false;
};

const cancelScheduledCheckoutV2ExperimentGateReset = () => {
  if (checkoutV2ExperimentGateResetTimeout) {
    clearTimeout(checkoutV2ExperimentGateResetTimeout);
    checkoutV2ExperimentGateResetTimeout = undefined;
  }
};

const scheduleCheckoutV2ExperimentGateReset = () => {
  cancelScheduledCheckoutV2ExperimentGateReset();
  checkoutV2ExperimentGateResetTimeout = setTimeout(() => {
    if (checkoutV2FlowConsumerCount === 0) {
      resetCheckoutV2ExperimentGate();
    }
    checkoutV2ExperimentGateResetTimeout = undefined;
  }, 0);
  checkoutV2ExperimentGateResetTimeout.unref?.();
};

export const clearCheckoutV2ExperimentGateForTests = () => {
  cancelScheduledCheckoutV2ExperimentGateReset();
  checkoutV2FlowConsumerCount = 0;
  resetCheckoutV2ExperimentGate();
};

export const resolveCheckoutV2ExperimentGate = ({
  isExperimentLoading,
  pathname,
  variantValue,
}: ResolveCheckoutV2ExperimentGateArgs): CheckoutV2ExperimentGate => {
  const isCheckoutFlowRoute = isCheckoutV2FlowRoute(pathname);

  if (lockedCheckoutV2TreatmentDecision !== undefined) {
    return {
      isReady: true,
      isTreatment: lockedCheckoutV2TreatmentDecision,
    };
  }

  const hasResolvedVariant = variantValue === 'control' || variantValue === 'treatment';
  const isReady = hasResolvedVariant || !isExperimentLoading;

  if (!isReady) {
    return {
      isReady: false,
      isTreatment: false,
    };
  }

  const isTreatment = variantValue === 'treatment';

  if (isCheckoutFlowRoute) {
    lockedCheckoutV2TreatmentDecision = isTreatment;
  }

  return {
    isReady: true,
    isTreatment,
  };
};

export const useCheckoutV2ExperimentGate = (): CheckoutV2ExperimentGate => {
  const { getVariantValue, isLoading, logExposure } = useExperiments();
  const pathname = usePathname();
  const checkoutV2Variant = getVariantValue(RunningExperiments.CheckoutV2);
  const gate = resolveCheckoutV2ExperimentGate({
    isExperimentLoading: isLoading,
    pathname,
    variantValue: checkoutV2Variant?.value,
  });

  useEffect(() => {
    if (!isCheckoutV2FlowRoute(pathname)) {
      cancelScheduledCheckoutV2ExperimentGateReset();
      resetCheckoutV2ExperimentGate();
      return undefined;
    }

    checkoutV2FlowConsumerCount += 1;
    cancelScheduledCheckoutV2ExperimentGateReset();

    return () => {
      checkoutV2FlowConsumerCount = Math.max(checkoutV2FlowConsumerCount - 1, 0);
      if (checkoutV2FlowConsumerCount === 0) {
        scheduleCheckoutV2ExperimentGateReset();
      }
    };
  }, [pathname]);

  useEffect(() => {
    const variantMatchesLockedDecision =
      checkoutV2Variant?.value === (gate.isTreatment ? 'treatment' : 'control');

    if (
      pathname === routes.cart &&
      gate.isReady &&
      variantMatchesLockedDecision &&
      !hasLoggedCheckoutV2ExposureForLockedDecision
    ) {
      logExposure(RunningExperiments.CheckoutV2);
      hasLoggedCheckoutV2ExposureForLockedDecision = true;
    }
  }, [checkoutV2Variant?.value, gate.isReady, gate.isTreatment, logExposure, pathname]);

  return gate;
};

export const useEnableCheckoutV2Experiment = () => {
  return useCheckoutV2ExperimentGate().isTreatment;
};
